import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, computed, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { CalculatedOdds, OddsFormatterService, emptyCalculatedOdds } from '@frontend/sports/odds/feature';
import { RacingOptionMarketTypes } from '@frontend/sports/racing/feature/models';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { Store } from '@ngrx/store';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from 'packages/sports/common/betslip/base/services/betslip-messages-provider.service';
import { IBetslipRootState } from 'packages/sports/common/betslip/base/store/state';
import { BetslipType } from 'packages/sports/common/betslip/core/betslip-type';
import { BetslipPick } from 'packages/sports/common/betslip/core/picks/betslip-pick';
import { PickOddsState, QuickBetPickType } from 'packages/sports/common/betslip/core/picks/pick-models';
import { getLegsCount, isBetBuilderPick, isUnknownPickWithLegs } from 'packages/sports/common/betslip/core/utils';
import { Observable, map, withLatestFrom } from 'rxjs';

import { BetslipBetBuilderPick } from '../../../../core/picks/betslip-bet-builder-pick';
import { BaseBetslipGroupPick } from '../../../../core/picks/betslip-group-pick';
import { betslipPickStateSelector } from '../../../picks/selectors';
import { isPickEachWayCapable } from '../../../picks/services/betslip-pick-helpers';
import { oddsBoostAnimations } from '../../../reward-tokens/components/digital/odds-boost-animations';
import { selectSummaryFreeBetToken } from '../../../stake/components/digital/summary-stake.component';
import { BetslipSummaryService } from '../../../summary/services/betslip-summary.service';
import { getOddsBoostWinnings } from '../../../summary/utils/betslip-summary.utils';
import { BetslipTypesActions } from '../../../types/actions';
import { betslipTypeStateSelector } from '../../../types/selectors';
import { selectQuickBetErrorsState } from '../../quick-bet.selectors';

type OddsViewModel =
    | { oddsState: PickOddsState.Closed | PickOddsState.Locked }
    | { oddsState: PickOddsState.Open; totalOdds: CalculatedOdds; boostedOdds: CalculatedOdds | null };

@Component({
    selector: 'bs-quick-bet-pick-list',
    templateUrl: './quick-bet-pick-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./quick-bet-pick-list.component.scss'],
    animations: [oddsBoostAnimations],
    standalone: false,
})
export default class QuickBetPickListComponent {
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() isEpcotEnabled: boolean;
    picks = input.required<BetslipPick[]>();

    PICK_LIST_MAX_LINE_COUNT = 2;
    PICK_LIST_DIVIDER = ',';

    optionNamesForMultiplePicks = computed(() => {
        const optionNames: string[] = [];
        const picks = this.picks();
        for (const pick of picks) {
            if (BetslipBetBuilderPick.isPick(pick)) {
                optionNames.push(...pick.getLegs());
            } else if (BaseBetslipGroupPick.isPick(pick) || isUnknownPickWithLegs(pick)) {
                optionNames.push(...pick.getLegs().map((p) => p.optionName.name));
            } else {
                optionNames.push(pick.optionName.name);
            }
        }

        return optionNames;
    });

    pickTypeFromPickList = computed(() => {
        const pickListLength = this.picks()?.length;

        if (pickListLength === 1) {
            return this.isBetBuilderPick(this.picks()[0]) ? QuickBetPickType.BetBuilderPicks : QuickBetPickType.SinglePick;
        }

        return this.picks().some(this.isBetBuilderPick) ? QuickBetPickType.BetBuilderPlusPicks : QuickBetPickType.ComboPicks;
    });

    multiBetTitle = computed(() => {
        const picks = this.picks();
        const pickCount = getLegsCount(picks);
        const hasBetBuilderPicks = picks.some(this.isBetBuilderPick);

        return this.getMultiSectionTitle(this.isEpcotEnabled, pickCount, hasBetBuilderPicks);
    });

    buildABetTitle = computed(() => {
        const picks = this.picks();
        const legCount = getLegsCount(picks);
        if (this.isEpcotEnabled) {
            return interpolateDoubleBracesString(this.sitecore.betslip.linear.LegParlay, { count: legCount });
        }

        return this.sitecore.betslip.linear.BabSGPResultTitle;
    });

    showEachWayButton = false;
    showEachWayText = false;
    eachWayButtonTitle: string;
    isEachWaySelected = false;
    buttonDisable: boolean;
    closedText: string;

    quickBetPickTypes = QuickBetPickType;
    pickOddsState = PickOddsState;

    readonly freeBetToken$ = this.store.select(selectSummaryFreeBetToken);

    oddsViewModel$: Observable<OddsViewModel>;
    constructor(
        public sitecore: Sitecore,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) private messageProvider: IBetslipMessagesProviderService,
        private store: Store<IBetslipRootState>,
        private oddsFormatterService: OddsFormatterService,
        private summaryService: BetslipSummaryService,
        private changeDetector: ChangeDetectorRef,
    ) {
        this.oddsViewModel$ = this.summaryService.getQuickBetSummary$().pipe(
            withLatestFrom(this.store.select(selectQuickBetErrorsState)),
            map(([[, summary], errorState]) => {
                if (errorState.isClosed) {
                    return { oddsState: PickOddsState.Closed };
                } else if (errorState.isLocked) {
                    return { oddsState: PickOddsState.Locked };
                }

                return {
                    oddsState: PickOddsState.Open,
                    boostedOdds: getOddsBoostWinnings(summary).boostedOdds,
                    totalOdds: summary.totalOdds,
                };
            }),
        );
        this.eachWayButtonTitle = this.messageProvider.getMessages().common.EachWayAbbr;

        this.store
            .select(betslipTypeStateSelector)
            .pipe(withLatestFrom(this.store.select(betslipPickStateSelector)), takeUntilDestroyed())
            .subscribe(([typeState, pickState]) => {
                this.showEachWayButton =
                    BetslipType.Combo === typeState.base.currentSelectedType! && Object.values(pickState.pickList).some(isPickEachWayCapable);
                this.showEachWayText = typeState.comboBet.isEachWay;

                if (this.showEachWayButton) {
                    this.isEachWaySelected = typeState.comboBet.isEachWay;
                }
                this.buttonDisable = pickState.pickList.some((p) => p.market?.marketType?.toLowerCase() === RacingOptionMarketTypes.WinEwExtraPlace);

                this.changeDetector.markForCheck();
            });
        this.closedText = this.sitecore.betslip.messages.ClosedPick;
    }

    formattedOdds(odds: CalculatedOdds | null): string {
        if (odds === emptyCalculatedOdds) {
            return '-';
        }

        return this.oddsFormatterService.format(odds, { displayFormat: this.oddsDisplayFormat, isSummary: true });
    }

    onEachWayClick(event: MouseEvent) {
        event.stopPropagation();
        this.store.dispatch(BetslipTypesActions.setEachWay({ isEachWay: !this.isEachWaySelected }));
    }

    getMultiSectionTitle(isEpcotEnabled: boolean, pickCount: number, hasBetBuilderPicks: boolean): string {
        if (isEpcotEnabled) {
            if (hasBetBuilderPicks) {
                return interpolateDoubleBracesString(this.sitecore.betslip.linear.LegParlay, { count: pickCount });
            } else if (pickCount > 1) {
                return `${this.sitecore.betslip.linear.PickParlay} • ${interpolateDoubleBracesString(this.sitecore.betslip.linear.LegParlay, { count: pickCount })}`;
            }

            return this.sitecore.betslip.linear.PickParlay;
        }

        if (hasBetBuilderPicks) {
            return this.sitecore.betslip.linear.ParlaysHeader;
        }

        if (pickCount === 2) {
            return this.sitecore.betslip.linear.Double;
        }

        if (pickCount === 3) {
            return this.sitecore.betslip.linear.Treble;
        }

        return interpolateDoubleBracesString(this.sitecore.betslip.linear.Fold, { count: pickCount });
    }

    private isBetBuilderPick(pick: BetslipPick): boolean {
        return isBetBuilderPick(pick) || isUnknownPickWithLegs(pick);
    }
}
